/**
 * Work blocks: the rules that place blocks of focus time on your calendar.
 *
 * WHO DOES WHAT. Project Hub holds the blocks (the `work_blocks` collection)
 * and this file decides where they go. Google Apps Script does the two things
 * only Google can: it reads your busy time from every calendar you list (with
 * who-declined-what, which an iCal address does not carry) and it writes the
 * events. A run is: the script gathers busy time → asks the Worker to plan
 * (`/workblocks/<token>/plan`, which calls `planWeek` below) → writes what
 * came back → reports the results. See docs/work-blocks/README.md.
 *
 * NO TIMEZONES HERE. Everything is a local calendar date ("2026-10-19") plus
 * minutes from local midnight, as the script sees them in its own time zone.
 * The Worker runs in UTC and must never turn these into instants.
 *
 * NO IMPORTS on purpose: `npm test` runs this file directly under Node's type
 * stripping, and the Worker bundles it as is.
 */

/* -- Types ------------------------------------------------------------------ */

export type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
/** Monday first: a day's index here is its offset from the week's Monday. */
export const DAY_KEYS: DayKey[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DAY_LABEL: Record<DayKey, string> = {
  mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
};

/**
 * "project": time for that project's tasks; counts against the hours its
 * tasks are estimated at. "routine": protected time for recurring work that is
 * not on any task list; it uses up the week but never covers a task.
 */
export type BlockPurpose = "project" | "routine";

export interface WorkBlock {
  id: string;
  /** Overrides the title made from the project ("WB: <project>"). */
  title?: string;
  project?: string;
  purpose?: BlockPurpose;
  durationMin: number;
  /** How many of these a week. Absent = 1. */
  perWeek?: number;
  preferredDay?: DayKey | "";
  /** "HH:MM", tried first. */
  preferredTime?: string;
  /** Do not start before. Blank = the work day's start. */
  earliest?: string;
  /** Must FINISH by. Blank = the work day's end. */
  latest?: string;
  /** Eligible days. Empty = the default days. */
  days?: DayKey[];
  /** Absent = on. */
  enabled?: boolean;
  position?: number;
  /** Blank = a standing block, every week. A Monday ("YYYY-MM-DD") = a one-off
   *  for that week only, made by the week planner. */
  week?: string;
  /** One-offs: place on or before this day (the last deadline they serve). */
  finishBy?: string;
  /** One-offs: the tasks this time is for. */
  itemIds?: string[];
  /** Written back after each real run. */
  lastStatus?: string;
  lastSlots?: Slot[];
  lastRunAt?: string;
}

export interface BlockSettings {
  workStart: string;
  workEnd: string;
  defaultDays: DayKey[];
  /** How finely to scan for a free start, in minutes. */
  slotMin: number;
  titlePrefix: string;
  /** Length of the extra blocks the week planner proposes. */
  blockLenMin: number;
  /** Plan the coming week automatically the first time Project Hub is opened
   *  after `autoRunDay` at `autoRunTime`. */
  autoRun: boolean;
  autoRunDay: DayKey;
  autoRunTime: string;
  /** Local "YYYY-MM-DDTHH:MM" when automatic planning was first on. Planning
   *  times before it never count, so turning this on mid-week does not
   *  immediately replan the week you are in. */
  autoRunSince?: string;
}

export const DEFAULT_BLOCK_SETTINGS: BlockSettings = {
  workStart: "08:00",
  workEnd: "16:00",
  defaultDays: ["mon", "tue", "wed", "thu", "fri"],
  slotMin: 15,
  titlePrefix: "WB: ",
  blockLenMin: 60,
  autoRun: true,
  autoRunDay: "sun",
  autoRunTime: "17:00",
};

/** A stretch of one local day. `start`/`end` are minutes from midnight. */
export interface Slot {
  date: string;
  start: number;
  end: number;
}

/** A block already on the calendar that stays: in the past, or moved by you. */
export interface KeptSlot extends Slot {
  blockId: string;
}

export interface PlanInput {
  /** The Monday of the week being planned. */
  weekStart: string;
  /** Local today and the minute of the day it is now: nothing goes in the past. */
  today: string;
  nowMin: number;
  /** Meetings and other commitments, from every calendar checked. */
  busy: Slot[];
  kept: KeptSlot[];
  blocks: WorkBlock[];
  settings?: Partial<BlockSettings>;
  projectNames?: Record<string, string>;
}

export interface Placement extends Slot {
  blockId: string;
  title: string;
  project?: string;
}

export interface BlockResult {
  blockId: string;
  title: string;
  wanted: number;
  kept: number;
  placed: Slot[];
  missed: number;
  status: string;
}

export interface PlanOutput {
  weekStart: string;
  placements: Placement[];
  results: BlockResult[];
  /** Open minutes in the work day on the default days, from now, before any
   *  block is placed: what the week has room for. */
  freeMinutes: number;
}

/* -- Dates and times -------------------------------------------------------- */

const MS_DAY = 86_400_000;
const toUtc = (key: string): number => Date.UTC(+key.slice(0, 4), +key.slice(5, 7) - 1, +key.slice(8, 10));
const fromUtc = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** The calendar day of a date string; PocketBase dates carry a time after it. */
export const dayKey = (s?: string): string => (s ? s.slice(0, 10) : "");

export function addDays(key: string, n: number): string {
  return fromUtc(toUtc(key) + n * MS_DAY);
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayIndex(key: string): number {
  return (new Date(toUtc(key)).getUTCDay() + 6) % 7;
}

export const dayKeyOf = (date: string): DayKey => DAY_KEYS[weekdayIndex(date)];

export function mondayOf(key: string): string {
  return addDays(key, -weekdayIndex(key));
}

/** The week the Sunday run plans: the first Monday strictly after today. */
export function nextMonday(today: string): string {
  return addDays(mondayOf(today), 7);
}

export function weekDates(weekStart: string): string[] {
  return DAY_KEYS.map((_, i) => addDays(weekStart, i));
}

/** "HH:MM" or "H:MM" (also "9", "9am", "2:30pm") → minutes, or null. */
export function parseTime(value?: string | null): number | null {
  if (value == null) return null;
  let s = String(value).trim().toLowerCase();
  if (!s || s.startsWith("(")) return null;
  const ampm = /(am|pm)$/.exec(s)?.[1];
  if (ampm) s = s.slice(0, -2).trim();
  const [hRaw, mRaw = "0"] = s.split(":");
  let h = parseInt(hRaw, 10);
  const m = parseInt(mRaw, 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (ampm === "pm" && h < 12) h += 12;
  if (ampm === "am" && h === 12) h = 0;
  return h * 60 + m;
}

/** 510 → "8:30 AM". */
export function formatTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

/** "Tue 8:30 AM". */
export function formatSlot(slot: Pick<Slot, "date" | "start">): string {
  return `${DAY_LABEL[dayKeyOf(slot.date)]} ${formatTime(slot.start)}`;
}

/** "Oct 19 – 25", for a week's Monday. */
export function weekLabel(weekStart: string): string {
  const mon = new Date(toUtc(weekStart));
  const sun = new Date(toUtc(addDays(weekStart, 6)));
  const month = (d: Date) => d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  return mon.getUTCMonth() === sun.getUTCMonth()
    ? `${month(mon)} ${mon.getUTCDate()} – ${sun.getUTCDate()}`
    : `${month(mon)} ${mon.getUTCDate()} – ${month(sun)} ${sun.getUTCDate()}`;
}

/* -- Blocks ----------------------------------------------------------------- */

export function resolveSettings(s?: Partial<BlockSettings> | null): BlockSettings {
  const out: BlockSettings = { ...DEFAULT_BLOCK_SETTINGS };
  if (!s) return out;
  if (parseTime(s.workStart) != null) out.workStart = String(s.workStart);
  if (parseTime(s.workEnd) != null) out.workEnd = String(s.workEnd);
  if (Array.isArray(s.defaultDays) && s.defaultDays.length) {
    out.defaultDays = DAY_KEYS.filter((d) => s.defaultDays!.includes(d));
  }
  if (Number(s.slotMin) > 0) out.slotMin = Number(s.slotMin);
  if (typeof s.titlePrefix === "string") out.titlePrefix = s.titlePrefix;
  if (Number(s.blockLenMin) > 0) out.blockLenMin = Number(s.blockLenMin);
  if (typeof s.autoRun === "boolean") out.autoRun = s.autoRun;
  if (s.autoRunDay && DAY_KEYS.includes(s.autoRunDay)) out.autoRunDay = s.autoRunDay;
  if (parseTime(s.autoRunTime) != null) out.autoRunTime = String(s.autoRunTime);
  if (typeof s.autoRunSince === "string" && s.autoRunSince) out.autoRunSince = s.autoRunSince;
  return out;
}

export const isOneOff = (b: Pick<WorkBlock, "week">): boolean => !!b.week;
export const purposeOf = (b: Pick<WorkBlock, "purpose">): BlockPurpose => (b.purpose === "routine" ? "routine" : "project");
export const perWeekOf = (b: Pick<WorkBlock, "perWeek">): number => Math.max(1, Math.round(Number(b.perWeek) || 1));

export function blockTitle(b: WorkBlock, settings: BlockSettings, projectNames: Record<string, string> = {}): string {
  const own = b.title?.trim();
  if (own) return own;
  const name = b.project ? projectNames[b.project] : "";
  return name ? `${settings.titlePrefix}${name}` : "Work block";
}

/**
 * The blocks a week runs, in the order they get first pick: standing blocks by
 * their position, then that week's one-offs by deadline.
 */
export function blocksForWeek(blocks: WorkBlock[], weekStart: string): WorkBlock[] {
  const live = blocks.filter((b) => b.enabled !== false && Number(b.durationMin) > 0);
  const standing = live.filter((b) => !isOneOff(b)).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const oneOffs = live
    .filter((b) => b.week === weekStart)
    .sort((a, b) => (a.finishBy || "9999").localeCompare(b.finishBy || "9999") || (a.position ?? 0) - (b.position ?? 0));
  return [...standing, ...oneOffs];
}

/** Standing minutes a week, by project, for one purpose. */
export function standingMinutesByProject(blocks: WorkBlock[], purpose: BlockPurpose): Map<string, number> {
  const out = new Map<string, number>();
  for (const b of blocks) {
    if (isOneOff(b) || b.enabled === false || purposeOf(b) !== purpose) continue;
    const key = b.project || "";
    out.set(key, (out.get(key) ?? 0) + Number(b.durationMin || 0) * perWeekOf(b));
  }
  return out;
}

/* -- Placement -------------------------------------------------------------- */

const overlaps = (a: Slot, b: Slot): boolean => a.date === b.date && a.start < b.end && a.end > b.start;

/** Minutes of `slots` inside [from, to) on one day, with overlaps merged. */
function coveredMinutes(slots: Slot[], date: string, from: number, to: number): number {
  const parts = slots
    .filter((s) => s.date === date && s.end > from && s.start < to)
    .map((s) => [Math.max(s.start, from), Math.min(s.end, to)] as const)
    .sort((a, b) => a[0] - b[0]);
  let total = 0;
  let curEnd = from;
  for (const [s, e] of parts) {
    const start = Math.max(s, curEnd);
    if (e > start) { total += e - start; curEnd = e; }
  }
  return total;
}

/**
 * Where a week's blocks go. Ported from the Apps Script planner (v2), rule for
 * rule — first open slot, the preferred time tried first, never in the past,
 * each placement reserving its time against later blocks — with three changes:
 *
 *  1. `perWeek` places that many copies, one per day where it can. The sheet
 *     did this with duplicate rows, and they all landed on the first open day.
 *  2. Days are tried in a sensible order. A block with a preferred day tries
 *     it first and then the NEAREST days (the sheet fell back to Monday). A
 *     standing block with no preference tries the lightest day first, which is
 *     what spreads them out. A one-off tries the earliest day first: it is
 *     deadline work, so it goes ahead of the deadline.
 *  3. Blocks you kept (past ones, or ones you moved in Google) count toward
 *     their block's weekly number instead of being placed again.
 */
export function planWeek(input: PlanInput): PlanOutput {
  const settings = resolveSettings(input.settings);
  const names = input.projectNames ?? {};
  const workStart = parseTime(settings.workStart) ?? 8 * 60;
  const workEnd = parseTime(settings.workEnd) ?? 16 * 60;
  const dates = weekDates(input.weekStart);
  const busy: Slot[] = [...input.busy, ...input.kept];

  // How full each day is, for spreading: meetings and blocks inside the work day.
  const load = (date: string) => coveredMinutes(busy, date, workStart, workEnd);

  const usable = (date: string, start: number) =>
    date > input.today || (date === input.today && start >= input.nowMin);

  const freeMinutes = dates.reduce((n, date) => {
    if (!settings.defaultDays.includes(dayKeyOf(date)) || date < input.today) return n;
    const from = date === input.today ? Math.min(workEnd, Math.max(workStart, input.nowMin)) : workStart;
    return n + Math.max(0, workEnd - from - coveredMinutes(input.busy, date, from, workEnd));
  }, 0);

  const placements: Placement[] = [];
  const results: BlockResult[] = [];

  for (const block of blocksForWeek(input.blocks, input.weekStart)) {
    const title = blockTitle(block, settings, names);
    const duration = Math.round(Number(block.durationMin));
    const wanted = perWeekOf(block);
    const keptHere = input.kept.filter((k) => k.blockId === block.id);
    const usedDays = new Set(keptHere.map((k) => k.date));
    const allowed = block.days?.length ? block.days : settings.defaultDays;
    const earliest = parseTime(block.earliest) ?? workStart;
    const latest = parseTime(block.latest) ?? workEnd;
    const prefTime = parseTime(block.preferredTime);
    const prefIdx = block.preferredDay ? DAY_KEYS.indexOf(block.preferredDay) : -1;
    const finishBy = isOneOff(block) ? dayKey(block.finishBy) : "";

    const candidates = dates.filter((d) =>
      allowed.includes(dayKeyOf(d)) && d >= input.today && (!finishBy || d <= finishBy));

    const starts: number[] = [];
    if (prefTime != null) starts.push(prefTime);
    for (let t = earliest; t + duration <= latest; t += settings.slotMin) if (t !== prefTime) starts.push(t);

    const placed: Slot[] = [];
    let missed = 0;
    for (let n = keptHere.length; n < wanted; n++) {
      const order = [...candidates].sort((a, b) => {
        const used = Number(usedDays.has(a)) - Number(usedDays.has(b));
        if (used) return used;
        const ia = weekdayIndex(a);
        const ib = weekdayIndex(b);
        if (prefIdx >= 0) {
          const near = Math.abs(ia - prefIdx) - Math.abs(ib - prefIdx);
          if (near) return near;
        } else if (!isOneOff(block)) {
          const lighter = load(a) - load(b);
          if (lighter) return lighter;
        }
        return ia - ib;
      });

      let slot: Slot | null = null;
      for (const date of order) {
        for (const start of starts) {
          if (start < earliest || start + duration > latest || !usable(date, start)) continue;
          const candidate = { date, start, end: start + duration };
          if (busy.some((b) => overlaps(candidate, b))) continue;
          slot = candidate;
          break;
        }
        if (slot) break;
      }

      if (!slot) { missed++; continue; }
      busy.push(slot);
      usedDays.add(slot.date);
      placed.push(slot);
      placements.push({ ...slot, blockId: block.id, title, ...(block.project ? { project: block.project } : {}) });
    }

    results.push({
      blockId: block.id, title, wanted, kept: keptHere.length, placed, missed,
      status: statusText(placed, keptHere.length, missed),
    });
  }

  placements.sort((a, b) => a.date.localeCompare(b.date) || a.start - b.start);
  return { weekStart: input.weekStart, placements, results, freeMinutes };
}

/** "Tue 8:30 AM, Thu 1:00 PM · 1 kept · 1 found no slot". */
export function statusText(placed: Slot[], kept: number, missed: number): string {
  const parts: string[] = [];
  if (placed.length) parts.push([...placed].sort((a, b) => a.date.localeCompare(b.date) || a.start - b.start).map(formatSlot).join(", "));
  if (kept) parts.push(`${kept} kept`);
  if (missed) parts.push(placed.length || kept ? `${missed} found no slot` : "No open slot in the allowed window");
  return parts.join(" · ") || "Nothing to place";
}

/* -- Week planner ----------------------------------------------------------- */

export interface PlannableItem {
  id: string;
  status?: string;
  type?: string;
  start?: string;
  due?: string;
  estimateHours?: number;
}

/**
 * The tasks a week's plan should cover: open tasks and deliverables scheduled
 * or due that week, plus anything already late. Milestones are markers, not
 * work, so they are left out.
 */
export function inWeekPlan(item: PlannableItem, weekStart: string, today: string): boolean {
  if (item.status === "done" || item.type === "milestone") return false;
  const weekEnd = addDays(weekStart, 6);
  const scheduled = dayKey(item.start || item.due);
  const due = dayKey(item.due);
  const inWeek = (d: string) => !!d && d >= weekStart && d <= weekEnd;
  return inWeek(scheduled) || inWeek(due) || (!!scheduled && scheduled < today);
}

export interface ProjectNeed {
  /** Minutes of estimated tasks. */
  estimateMin: number;
  /** Tasks with no estimate yet: not counted, and flagged. */
  unestimated: number;
  /** Minutes of standing project-work blocks for this project. */
  standingMin: number;
  /** Estimate minus standing time, never negative. */
  gapMin: number;
  /** Extra blocks of `blockLenMin` that would cover the gap. */
  suggestedBlocks: number;
  /** The last deadline among the tasks inside the week: where extra time must
   *  be done by. Blank when none falls in the week. */
  finishBy: string;
}

export function projectNeed(
  items: PlannableItem[], standingMin: number, blockLenMin: number, weekStart: string,
): ProjectNeed {
  const estimateMin = Math.round(items.reduce((n, i) => n + (Number(i.estimateHours) > 0 ? Number(i.estimateHours) * 60 : 0), 0));
  const unestimated = items.filter((i) => !(Number(i.estimateHours) > 0)).length;
  const gapMin = Math.max(0, estimateMin - standingMin);
  const weekEnd = addDays(weekStart, 6);
  const dues = items.map((i) => dayKey(i.due)).filter((d) => d >= weekStart && d <= weekEnd).sort();
  return {
    estimateMin, unestimated, standingMin, gapMin,
    suggestedBlocks: blockLenMin > 0 ? Math.ceil(gapMin / blockLenMin) : 0,
    finishBy: dues[dues.length - 1] ?? "",
  };
}

/* Typed estimates are read by parseDuration in timeRollup.ts, the same rules
 * as every other duration in the app: "10" is minutes, "1:10" is h:mm. */

/** Minutes → "h:mm": 10 → "0:10", 70 → "1:10", 120 → "2:00". 0 → "". */
export function hmm(min: number): string {
  const m = Math.round(min);
  if (!(m > 0)) return "";
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
}

/** A task's stored estimate (hours) as h:mm. */
export const estimateLabel = (hours?: number): string => hmm((Number(hours) || 0) * 60);

/* -- Automatic planning ----------------------------------------------------- */

/** Local "YYYY-MM-DDTHH:MM": compares correctly as a plain string. */
export const localStamp = (date: string, min: number): string =>
  `${date}T${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

/**
 * The most recent planning time at or before now, and the week it plans: the
 * first Monday on or after it. Sunday 5 PM plans the next day's week; a
 * Monday 7 AM time plans the week it is in; Friday plans the week after.
 */
export function latestPlanningTime(today: string, nowMin: number, s: BlockSettings): { stamp: string; weekStart: string } {
  const runMin = parseTime(s.autoRunTime) ?? 17 * 60;
  let back = (weekdayIndex(today) - DAY_KEYS.indexOf(s.autoRunDay) + 7) % 7;
  if (back === 0 && nowMin < runMin) back = 7;
  const date = addDays(today, -back);
  return {
    stamp: localStamp(date, runMin),
    weekStart: weekdayIndex(date) === 0 ? date : addDays(mondayOf(date), 7),
  };
}

/**
 * The week to plan automatically right now, or "" for none: when it is on,
 * the latest planning time has passed since it was turned on, and that week
 * has not been planned yet (by an automatic run or by "Send to Google").
 */
export function autoRunDue(today: string, nowMin: number, s: BlockSettings, weeksRun: string[]): string {
  if (!s.autoRun || !s.autoRunSince) return "";
  const { stamp, weekStart } = latestPlanningTime(today, nowMin, s);
  if (stamp < s.autoRunSince) return "";
  return weeksRun.includes(weekStart) ? "" : weekStart;
}
